import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatTRY } from "@/lib/api";
import { Printer, ArrowLeft, CarFront } from "lucide-react";

const MONTH_LABELS = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
];

// Ferah, büyük yazılı satır
const Row = ({ label, value, bold, tone }) => (
  <tr className={bold ? "border-t-2 border-slate-900" : "border-b border-slate-100"}>
    <td className={`py-2 pr-3 text-base ${bold ? "font-bold text-slate-900" : "text-slate-700"}`}>
      {label}
    </td>
    <td
      className={`py-2 pl-3 text-right font-mono-num text-base whitespace-nowrap ${
        bold ? "font-bold text-lg" : ""
      } ${
        tone === "emerald" ? "text-emerald-700" :
        tone === "rose" ? "text-rose-700" :
        tone === "blue" ? "text-blue-700" : "text-slate-900"
      }`}
    >
      {value}
    </td>
  </tr>
);

const Section = ({ title, children }) => (
  <div className="mb-5 avoid-break">
    <h2 className="text-sm uppercase tracking-widest font-bold text-slate-800 border-b-2 border-slate-800 pb-1 mb-2">
      {title}
    </h2>
    <table className="w-full">
      <tbody>{children}</tbody>
    </table>
  </div>
);

export default function MonthlyReportPrint() {
  const [params] = useSearchParams();
  const now = new Date();
  const monthParam = parseInt(params.get("month") || String(now.getMonth() + 1), 10);
  const yearParam = parseInt(params.get("year") || String(now.getFullYear()), 10);

  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const key = `${yearParam.toString().padStart(4, "0")}-${monthParam.toString().padStart(2, "0")}`;
    api
      .get(`/dashboard/summary?month=${key}`)
      .then((r) => setData(r.data))
      .catch(() => setErr("Yüklenemedi"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data) setTimeout(() => window.print(), 500);
  }, [data]);

  if (err) return <div className="p-8 text-center text-rose-600">{err}</div>;
  if (!data) return <div className="p-8 text-center text-slate-500">Yükleniyor…</div>;

  const monthLabel = `${MONTH_LABELS[monthParam - 1]} ${yearParam}`;
  const cm = data.current_month;
  const bal = data.balance;
  const ins = data.insurance;
  const sal = data.salary_status;
  const jobsBucket = (data.jobs_summary && data.jobs_summary.by_status_month) || {
    beklemede: { count: 0, total: 0 },
    teslim_edildi: { count: 0, total: 0 },
    kismi_odeme: { count: 0, total: 0 },
    carried_over: { count: 0, total: 0 },
  };
  const printedAt = new Date().toLocaleString("tr-TR");

  return (
    <div className="bg-white min-h-screen text-slate-900">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .avoid-break { break-inside: avoid; page-break-inside: avoid; }
        }
        @page { size: A4 portrait; margin: 10mm; }
      `}</style>

      <div className="no-print bg-slate-900 text-white px-6 py-3 flex items-center justify-between sticky top-0 z-50">
        <a href="/" className="inline-flex items-center gap-2 text-sm hover:text-blue-300" data-testid="report-back">
          <ArrowLeft className="w-4 h-4" /> Özete Dön
        </a>
        <button
          data-testid="report-print-btn"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-md text-sm font-medium"
        >
          <Printer className="w-4 h-4" /> Yazdır / PDF Kaydet
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-6 print:p-0" data-testid="monthly-report-doc">
        {/* Header — büyük ve ferah */}
        <div className="flex items-center justify-between border-b-2 border-slate-900 pb-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded bg-slate-900 flex items-center justify-center">
              <CarFront className="w-6 h-6 text-white" strokeWidth={2.2} />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold leading-tight">Global Car Service</h1>
              <p className="text-xs text-slate-600 leading-tight mt-0.5">Kaporta · Boya · Muhasebe Raporu</p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-display text-xl font-bold leading-tight">{monthLabel}</p>
            <p className="text-xs text-slate-500 leading-tight mt-0.5">Basım: {printedAt}</p>
          </div>
        </div>

        {/* Öne çıkan 3 kutu — büyük */}
        <div className="grid grid-cols-3 gap-3 mb-6 avoid-break">
          <div className="border-2 border-emerald-300 bg-emerald-50 rounded px-3 py-3">
            <p className="text-xs uppercase tracking-widest text-emerald-800 font-semibold">Ay Geliri</p>
            <p className="font-display text-2xl font-bold text-emerald-700 font-mono-num mt-1">{formatTRY(cm.total_income || cm.income)}</p>
          </div>
          <div className="border-2 border-rose-300 bg-rose-50 rounded px-3 py-3">
            <p className="text-xs uppercase tracking-widest text-rose-800 font-semibold">Ay Gideri</p>
            <p className="font-display text-2xl font-bold text-rose-700 font-mono-num mt-1">{formatTRY(cm.expense)}</p>
          </div>
          <div className={`border-2 rounded px-3 py-3 ${cm.profit >= 0 ? "border-slate-900 bg-slate-50" : "border-rose-400 bg-rose-50"}`}>
            <p className="text-xs uppercase tracking-widest text-slate-800 font-semibold">Ay Net Kâr</p>
            <p className={`font-display text-2xl font-bold font-mono-num mt-1 ${cm.profit >= 0 ? "text-slate-900" : "text-rose-700"}`}>
              {formatTRY(cm.profit)}
            </p>
          </div>
        </div>

        {/* Dikey akış — tek sütun, sade */}
        <div>
          <Section title={`Gelir · ${monthLabel}`}>
            <Row label="İş Tahsilatı (KDV dahil)" value={formatTRY(cm.income)} tone="emerald" />
            <Row label="Sigorta Tahsilatı" value={formatTRY(cm.ins_collected || 0)} tone="emerald" />
            <Row bold label="TOPLAM GELİR" value={formatTRY(cm.total_income || cm.income)} tone="emerald" />
          </Section>

          <Section title={`Gider · ${monthLabel}`}>
            <Row label="Maaş" value={formatTRY(cm.salary)} tone="rose" />
            <Row label="Malzeme (Parça)" value={formatTRY(cm.supply_parca || 0)} tone="rose" />
            <Row label="Malzeme (Boyahane)" value={formatTRY(cm.supply_boyahane || 0)} tone="rose" />
            <Row label="Malzeme (Kaporta)" value={formatTRY(cm.supply_kaporta || 0)} tone="rose" />
            <Row label="Dükkan Giderleri" value={formatTRY(cm.shop || 0)} tone="rose" />
            <Row bold label="TOPLAM GİDER" value={formatTRY(cm.expense)} tone="rose" />
          </Section>

          <Section title="Bekleyen Alacaklar (Tahsil Edilmemiş)">
            <Row
              label={`Bekleyen İş Kaydı (${jobsBucket.beklemede.count})`}
              value={formatTRY(jobsBucket.beklemede.total)}
              tone="rose"
            />
            <Row
              label={`Teslim Edildi · Ödeme Bekliyor (${jobsBucket.teslim_edildi.count})`}
              value={formatTRY(jobsBucket.teslim_edildi.total)}
              tone="rose"
            />
            <Row
              label={`Kısmi Ödeme · Kalan (${jobsBucket.kismi_odeme.count})`}
              value={formatTRY(jobsBucket.kismi_odeme.total)}
              tone="rose"
            />
            {jobsBucket.carried_over && jobsBucket.carried_over.count > 0 && (
              <Row
                label={`↳ Geçmiş aylardan devir (${jobsBucket.carried_over.count})`}
                value={formatTRY(jobsBucket.carried_over.total)}
                tone="rose"
              />
            )}
            <Row
              label={`Bekleyen Sigorta Alacağı (${ins.active_count})`}
              value={formatTRY(ins.total_pending)}
              tone="rose"
            />
            {ins.carried_over && ins.carried_over.count > 0 && (
              <Row
                label={`↳ Geçmiş aylardan sigorta devri (${ins.carried_over.count})`}
                value={formatTRY(ins.carried_over.total)}
                tone="rose"
              />
            )}
            <Row
              bold
              label="TOPLAM BEKLEYEN ALACAK"
              value={formatTRY(
                (jobsBucket.beklemede.total || 0) +
                (jobsBucket.teslim_edildi.total || 0) +
                (jobsBucket.kismi_odeme.total || 0) +
                (ins.total_pending || 0)
              )}
              tone="blue"
            />
          </Section>

          <Section title="Genel Bakiye">
            <Row label="Nakit giriş (tüm zaman)" value={formatTRY(bal.cash_in)} tone="emerald" />
            <Row label="Nakit çıkış (tüm zaman)" value={formatTRY(bal.cash_out)} tone="rose" />
            <Row label="Net Kasa" value={formatTRY(bal.net_bakiye)} tone={bal.net_bakiye >= 0 ? "emerald" : "rose"} />
            <Row label="Toplam Alacak" value={formatTRY(bal.total_alacak)} tone="blue" />
            <Row label="Toplam Borç" value={formatTRY(bal.total_borc)} tone="rose" />
            <Row
              bold
              label="GENEL TOPLAM BAKİYE"
              value={formatTRY(bal.genel_bakiye)}
              tone={bal.genel_bakiye >= 0 ? "emerald" : "rose"}
            />
          </Section>
        </div>

        {/* İmza bandı */}
        <div className="grid grid-cols-2 gap-16 mt-10 pt-4 avoid-break">
          <div className="text-center border-t border-slate-400 pt-2">
            <p className="text-xs text-slate-600">Hazırlayan</p>
          </div>
          <div className="text-center border-t border-slate-400 pt-2">
            <p className="text-xs text-slate-600">Onaylayan · Kaşe</p>
          </div>
        </div>

        <p className="text-center text-[10px] text-slate-500 mt-4">
          Global Car Service Muhasebe · Otomatik oluşturulmuştur.
        </p>
      </div>
    </div>
  );
}
